import { useState } from 'react'
import { Tabs } from '@/components/ui/primitives'
import { BranchesSection } from '@/pages/settings/BranchesSection'
import { CategoriesSection } from '@/pages/settings/CategoriesSection'
import { SuppliersSection } from '@/pages/settings/SuppliersSection'
import { UsersSection } from '@/pages/settings/UsersSection'
import { PrinterSection } from '@/pages/settings/PrinterSection'
import { ExchangeRateSection } from '@/pages/settings/ExchangeRateSection'
import { VatSection } from '@/pages/settings/VatSection'
import { ReceiptSection } from '@/pages/settings/ReceiptSection'
import { DrawersSection } from '@/pages/settings/DrawersSection'
import { LabelsSection } from '@/pages/settings/LabelsSection'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

type TabValue =
  | 'branches'
  | 'rate'
  | 'vat'
  | 'receipt'
  | 'categories'
  | 'suppliers'
  | 'users'
  | 'drawers'
  | 'labels'
  | 'printer'

export function SettingsPage() {
  const can = useSession((state) => state.can)
  const canManageUsers = can(Permission.ManageUsers)
  const canManageCatalog = can(Permission.ManageCatalog)
  const canSeeDrawers = can(Permission.ManageSettings) || can(Permission.ManageSafe)

  const tabs = [
    { value: 'branches' as const, label: t('Branches') },
    // Visible to everyone: a cashier needs to see the rate they are taking lira at, even
    // though only an administrator can move it.
    { value: 'rate' as const, label: t('LBP rate') },
    { value: 'vat' as const, label: t('VAT') },
    { value: 'receipt' as const, label: t('Receipt') },
    ...(canManageCatalog
      ? [
          { value: 'categories' as const, label: t('Categories') },
          { value: 'suppliers' as const, label: t('Suppliers') },
        ]
      : []),
    ...(canManageUsers ? [{ value: 'users' as const, label: t('Users') }] : []),
    ...(canSeeDrawers ? [{ value: 'drawers' as const, label: t('Drawers') }] : []),
    ...(canManageCatalog ? [{ value: 'labels' as const, label: t('Barcodes') }] : []),
    // Every cashier needs this: the printer is a property of the device they are holding.
    { value: 'printer' as const, label: t('Printer') },
  ]

  const [tab, setTab] = useState<TabValue>('branches')

  return (
    <div className="pb-page mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
      <header className="mb-5">
        <h1 className="text-xl font-semibold">{t('Settings')}</h1>
      </header>

      <div className="mb-5">
        <Tabs tabs={tabs} value={tab} onChange={setTab} />
      </div>

      {tab === 'branches' && <BranchesSection />}
      {tab === 'rate' && <ExchangeRateSection />}
      {tab === 'vat' && <VatSection />}
      {tab === 'receipt' && <ReceiptSection />}
      {tab === 'categories' && canManageCatalog && <CategoriesSection />}
      {tab === 'suppliers' && canManageCatalog && <SuppliersSection />}
      {tab === 'users' && canManageUsers && <UsersSection />}
      {tab === 'drawers' && canSeeDrawers && <DrawersSection />}
      {tab === 'labels' && canManageCatalog && <LabelsSection />}
      {tab === 'printer' && <PrinterSection />}
    </div>
  )
}
